import apiService from './api';
import { todayYmd } from '../utils/appTimezone';

const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** API espace réception (recherche adhérent, visites, orientation médecin) */

const receptionService = {
  /** Recherche exacte par matricule (adhérents actifs). */
  async lookupMemberByMatricule(matricule) {
    const q = encodeURIComponent(String(matricule).trim());
    return apiService.get(`/reception/members/by-matricule?matricule=${q}`);
  },

  /** Recherche par matricule, nom ou prénom (adhérents actifs, max ~30 résultats). */
  async searchMembers(q, opts = {}) {
    const params = new URLSearchParams();
    params.append('q', String(q).trim());
    if (opts.limit) params.append('limit', String(opts.limit));
    return apiService.get(`/reception/members/search?${params.toString()}`);
  },

  /** Création groupée: agent + ayants droit. */
  async createHousehold(payload) {
    return apiService.post('/reception/members/household', payload);
  },

  /** Mise à jour limitée d'un adhérent actif (identité) depuis l'accueil. */
  async updateMemberProfile(id, payload) {
    return apiService.patch(`/reception/members/${id}`, payload);
  },

  async getDoctors() {
    return apiService.get('/reception/doctors');
  },

  /** @param {'MEDICAL'|'DENTAL'|'NURSING'} serviceLine */
  async getOrientationStaff(serviceLine) {
    const line = encodeURIComponent(serviceLine);
    return apiService.get(`/reception/orientation-staff?service_line=${line}`);
  },

  async getSettings() {
    return apiService.get('/reception/settings');
  },

  async putSettings(payload) {
    return apiService.put('/reception/settings', payload);
  },

  async getSites() {
    return apiService.get('/reception/sites');
  },

  /** Horloge référence MySQL NOW() + infos fuseau (option RECEPTION_CLOCK_TZ côté API). */
  async getServerTime() {
    return apiService.get('/reception/server-time');
  },

  /** Agrégats pour le tableau de bord (jour, veille, 7 j, filières). */
  async getDashboardStats() {
    return apiService.get('/reception/dashboard/stats');
  },

  /**
   * @param {{ from?: string, to?: string, status?: string, service_line?: string, q?: string, today?: boolean, company_id?: number, limit?: number, offset?: number }} filters
   */
  async getVisits(filters = {}) {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.status) params.append('status', filters.status);
    if (filters.service_line) params.append('service_line', String(filters.service_line));
    if (filters.q) params.append('q', String(filters.q).trim());
    if (filters.today === false) params.append('today', 'false');
    if (filters.company_id != null) params.append('company_id', String(filters.company_id));
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.offset != null) params.append('offset', String(filters.offset));
    const qs = params.toString();
    return apiService.get(`/reception/visits${qs ? `?${qs}` : ''}`);
  },

  async registerVisit(payload) {
    return apiService.post('/reception/visits', payload);
  },

  async registerAndOrientVisit(payload) {
    return apiService.post('/reception/visits/intake', payload);
  },

  async orientVisit(visitId, payload) {
    return apiService.post(`/reception/visits/${visitId}/orient`, payload);
  },

  /** Consultations créées aujourd'hui après orientation depuis l'accueil. */
  async getOrientedConsultationsToday() {
    return apiService.get('/reception/consultations/oriented-today');
  },

  /** Historique des passages réception pour un matricule. */
  async getVisitHistoryByMatricule(matricule, opts = {}) {
    const params = new URLSearchParams({
      matricule: String(matricule || '').trim()
    });
    if (opts.limit) params.append('limit', String(opts.limit));
    return apiService.get(`/reception/visits/history-by-matricule?${params.toString()}`);
  },

  /** Télécharge le CSV des visites (mêmes filtres que le journal ; jour courant par défaut). */
  async downloadVisitsCsv(filters = {}) {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.status) params.append('status', filters.status);
    if (filters.service_line) params.append('service_line', String(filters.service_line));
    if (filters.q) params.append('q', String(filters.q).trim());
    if (filters.today === false) params.append('today', 'false');
    if (filters.company_id != null) params.append('company_id', String(filters.company_id));
    const qs = params.toString();
    const token =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null;
    const url = `${API_ROOT}/reception/visits/export.csv${qs ? `?${qs}` : ''}`;
    const r = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!r.ok) {
      let msg = 'Export CSV impossible.';
      try {
        const data = await r.json();
        if (data.message) msg = data.message;
      } catch (_) {
        /* ignore non-JSON error body */
      }
      throw new Error(msg);
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    const fromLabel = filters.from ? String(filters.from).slice(0, 10) : todayYmd();
    const toLabel = filters.to ? String(filters.to).slice(0, 10) : fromLabel;
    const fname =
      fromLabel === toLabel
        ? `visites_reception_${fromLabel}.csv`
        : `visites_reception_${fromLabel}_${toLabel}.csv`;
    a.href = URL.createObjectURL(blob);
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  }
};

export default receptionService;